// Strings of network runs and the shell: the network run page and plan review, the runs list's network filters and
// Target column, the palette's network commands, the breadcrumb's Network switcher, network activity sentences (spec 11
// 6.1.7, 6.1.11, 6.9, 6.11, 6.15.11, 6.15.15–6.15.17). `strings.networks.runs`.
import type { NetworkActivityKind } from '../../api/types';

const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

export const networkRuns = {
  // Run page of a network run (6.15.11).
  title: (kind: string, network: string) => `${kind} · ${network}`,
  network: 'Network',
  forDeployment: 'for',
  queuedAfter: 'queued after',
  parentRun: 'its parent run',
  openNetwork: 'Open network',
  scopeAttachment: 'attachment',
  // Plan review of a network plan.
  current: 'This plan is current.',
  currentDetail: (hash: string, network: string) => `Same settings (hash ${hash}) as ${network} now.`,
  stale: 'This plan is out of date:',
  staleReasons: {
    settingsChanged: 'the network’s settings changed since',
    appliedAfter: 'the network was applied after this plan',
  },
  impact: 'Impact',
  componentTypes: {
    'network.region': 'Region',
    'network.zones': 'Private DNS zones',
    'network.attachment': 'Attachment',
    'network.link': 'Link',
    'network.request': 'Network request',
    'network.entry-point': 'Entry point',
  } as Record<string, string>,
  attachmentResource: (deployment: string, provider: string, region: string) =>
    `${deployment} · ${provider} / ${region}`,
  regionResource: (provider: string, region: string) => `${provider} · ${region}`,
  entryPointResource: (id: string) => `Entry point ${id}`,
  endpointTitle: 'New addresses',
  endpointChanges: (component: string) =>
    `${component} gets a new host name or IP: update the DNS records and clients that point to it.`,
  outageReplaced: (component: string) => `${component} is re-created: traffic through it stops briefly.`,
  outageDeleted: (component: string) => `${component} is deleted: traffic through it stops.`,
  typedNote: (network: string) => `Applying this plan asks you to type ${network}.`,
  appliesWait: (n: number) =>
    n === 1
      ? 'The apply of the 1 attached deployment waits while this apply runs.'
      : `Applies of the ${n} attached deployments wait while this apply runs.`,
  noAttached: 'No attached deployment waits for this apply.',
  noOutage: 'No component is re-created or deleted.',
  deploymentsToApply: 'Apply these deployments:',
  components: 'Components',
  noComponents: 'The plan changes no component.',
  // An attached deployment's plan review (6.15.16, mock n07).
  resourcesChips: 'Resources',
  networkChips: 'Network',
  networkChipsLabel: 'Filter spoke changes by action',
  beforeApplyInterrupted: (queued: number) =>
    queued > 0
      ? `Private connections are interrupted until the connect run (${plural(queued, 'run')} queued before it).`
      : 'Private connections are interrupted until the connect run.',
  networkSection: 'Network',
  attachTo: (network: string, provider: string, regions: string) =>
    `attach to ${network} · ${provider} / ${regions}`,
  spoke: (deployment: string) => `Spoke (${deployment}’s network)`,
  noSpokeChanges: 'The spoke doesn’t change.',
  noSpokeRows: 'No spoke change matches the filter.',
  connects: (deployment: string, network: string) =>
    `After this apply, Nebisys connects ${deployment} to ${network}`,
  noSteps: 'Nothing to connect.',
  typeLabels: {
    VirtualNetwork: 'virtual network',
    NetworkSecurityGroup: 'network security group',
    RouteTable: 'route table',
    PrivateZone: 'private DNS zone',
    VirtualNetworkLink: 'private DNS link',
    PrivateRecordSet: 'private DNS record',
    Subnet: 'subnet',
    PrivateEndpoint: 'private endpoint',
    // AWS (feature spec 17, phase 4)
    Vpc: 'VPC',
    InternetGateway: 'internet gateway',
    SecurityGroup: 'security group',
    Role: 'IAM role',
    SubnetGroup: 'DB subnet group',
    // Google Cloud (feature spec 17, phase 6)
    Network: 'VPC',
    Subnetwork: 'subnet',
    GlobalAddress: 'private services access range',
    Connection: 'private services access connection',
    Router: 'Cloud Router',
    RouterNat: 'Cloud NAT',
    Spoke: 'Network Connectivity Center spoke',
    IAMCustomRole: 'grant role',
    IAMMember: 'grant role binding',
    // AWS entry points (feature spec 17, phase 5)
    NatGateway: 'NAT gateway',
    VpcOrigin: 'CloudFront VPC origin',
    ResourceShare: 'RAM resource share',
    CrossAccountAttachment: 'Global Accelerator cross-account attachment',
  } as Record<string, string>,
  // After-apply callout (6.15.15).
  applied: (deployment: string, network: string) =>
    `Applied. Private connections inside ${deployment} work again when Nebisys has connected ${deployment} to ${network}`,
  queuedBehind: (n: number) => `connect run queued behind ${plural(n, 'run')}`,
  connectRunning: 'connect run running',
  connectQueued: 'connect run queued',
  connected: (network: string) => `Connected to ${network}: private connections work again.`,
  connectFailed: (network: string) => `Connecting to ${network} failed.`,
  retrying: (time: string) => `retrying at ${time} (waiting for permissions)`,
  retryingSoon: 'retrying soon (waiting for permissions)',
  open: 'Open',
  openConnectRun: 'Open the connect run',
  retry: 'Retry',
  retryQueued: 'The connect run is queued.',
  // Runs list (6.9).
  subtitle: 'Plans, applies, refreshes and destroys of every deployment and network, newest first.',
  targetFilter: 'Target',
  targetValues: { deployment: 'Deployments', network: 'Networks' },
  networkFilter: 'Network',
  target: 'Target',
  networkBelow: 'Network',
  networkChanges: 'network',
  networkChangeCount: (label: string, n: number) =>
    `${n} network ${n === 1 ? 'change' : 'changes'} to ${label.toLowerCase()}`,
  // Active-runs pill (6.1.7).
  pillItem: (kind: string, network: string) => `${kind} · ${network}`,
  // Breadcrumb (6.1.7).
  networkSwitcher: (name: string | null, status: string | null) =>
    name ? `Network ${name}${status ? `, ${status}` : ''}` : 'Network',
  // Activity (6.11).
  activity: {
    group: 'Network',
    actor: 'Nebisys (network)',
    kinds: {
      'network-created': 'Network created',
      'network-changed': 'Network settings changed',
      'network-deleted': 'Network deleted',
      'network-attached': 'Deployment attached',
      'network-detached': 'Deployment detached',
      'network-connected': 'Deployment connected',
      'network-connect-failed': 'Connect failed',
      'network-addresses-released': 'Addresses released',
    } satisfies Record<NetworkActivityKind, string>,
    created: 'created the network',
    changed: 'changed the network’s settings',
    deleted: 'deleted the network',
    attached: 'attached',
    detached: 'detached',
    connected: 'connected',
    connectFailed: 'couldn’t connect',
    released: 'released',
    addressesIn: 'addresses in',
    to: 'to',
    from: 'from',
    of: 'of',
  },
};
